import { FC, useState, useEffect } from 'react';
import UploadDocuments from '@/pages/Facilities/components/UploadDocuments';
import useGetBusinessDocuments from '@/api/hooks/useGetBusinessDocuments';
import { useBusinessId } from '@/utils/hooks/useBusinessId';
import { useHandleDocument } from '@/utils/hooks/useHandleDocument';
import type { BusinessDocumentType, BusinessDocument } from '@/types/business';

export interface DocumentInfo {
  isUploaded: boolean;
  error?: string;
  businessDocument?: BusinessDocument<BusinessDocumentType>;
}

interface Props {
  userId?: string;
  facilityId?: string;
  label: string;
  type: BusinessDocumentType;
  isRequired?: boolean;
  setDocumentInfo?: (info: DocumentInfo) => void;
  documentInfo?: DocumentInfo;
}

const UploadDocumentFile: FC<Props> = ({
  userId,
  facilityId,
  label,
  type,
  isRequired = false,
  setDocumentInfo,
  documentInfo,
}): React.ReactElement => {
  const businessId = useBusinessId();
  const [disableUploadButton, setDisableUploadButton] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { data: documentList, refetch: refetchDocuments } =
    useGetBusinessDocuments<BusinessDocumentType>(
      businessId ?? '',
      facilityId,
      userId,
      {
        enabled: businessId != null,
      },
    );

  const businessDocument = documentList?.find(
    (document) => document.documentType === type,
  );

  const handleDocument = useHandleDocument<BusinessDocumentType>({
    onUploadSuccess(documentType, syncteraDocumentId, fileName) {
      refetchDocuments({ throwOnError: true })
        .then(() => {
          setDisableUploadButton(false);
          setErrorMessage(undefined);

          const doc: BusinessDocument<BusinessDocumentType> = {
            businessId: businessId!,
            documentType,
            fileName,
            syncteraDocumentId,
          };

          setDocumentInfo?.({
            isUploaded: true,
            error: undefined,
            businessDocument: doc,
          });
        })
        .catch(console.error);
    },
    onUpdateSuccess() {
      refetchDocuments({ throwOnError: true })
        .then(() => {
          setDisableUploadButton(false);
          setErrorMessage(undefined);

          const typedDocument: BusinessDocument<BusinessDocumentType> =
            businessDocument!;

          if (businessDocument) {
            setDocumentInfo?.({
              isUploaded: true,
              error: undefined,
              businessDocument: typedDocument,
            });
          }
        })
        .catch(console.error);
    },
    onError(error) {
      setDisableUploadButton(false);
      setErrorMessage(error.message);

      setDocumentInfo?.({
        isUploaded: false,
        error: error.message,
        businessDocument,
      });
    },
  });

  const handleUpload: React.ComponentProps<
    typeof UploadDocuments<BusinessDocumentType>
  >['onFileUpload'] = (documentType, file) => {
    if (businessId == null) return;
    setDisableUploadButton(true);
    handleDocument.handleFileUpload({
      documentType,
      file,
      businessId,
      facilityId,
      ownerId: userId,
    });
  };

  const handleUpdate: React.ComponentProps<
    typeof UploadDocuments<BusinessDocumentType>
  >['onFileUpdate'] = (documentType, file, documentId) => {
    if (businessId == null) return;
    setDisableUploadButton(true);
    handleDocument.handleFileUpdate({
      documentType,
      file,
      businessId,
      facilityId,
      ownerId: userId,
      documentId,
    });
  };

  useEffect(() => {
    if (!setDocumentInfo) return;

    const docInfo: DocumentInfo = {
      isUploaded: !!businessDocument,
      error: errorMessage,
      businessDocument,
    };

    const infoChanged =
      documentInfo?.isUploaded !== docInfo.isUploaded ||
      documentInfo?.error !== docInfo.error ||
      documentInfo?.businessDocument?.syncteraDocumentId !==
        docInfo.businessDocument?.syncteraDocumentId;

    if (infoChanged) {
      setDocumentInfo(docInfo);
    }
  }, [businessDocument, errorMessage, documentInfo, setDocumentInfo]);

  return (
    <>
      <UploadDocuments
        required={isRequired}
        documentType={type}
        label={label}
        disabled={disableUploadButton}
        fileName={
          businessDocument?.fileName ??
          documentInfo?.businessDocument?.fileName ??
          ''
        }
        documentId={
          businessDocument?.syncteraDocumentId ??
          documentInfo?.businessDocument?.syncteraDocumentId ??
          ''
        }
        onFileUpdate={handleUpdate}
        onFileUpload={handleUpload}
        errorMessage={errorMessage}
      />
      {handleDocument.renderUploadFileLoading()}
    </>
  );
};

export default UploadDocumentFile;
